"use client";

import { ExclamationCircleIcon } from "@heroicons/react/24/solid";

import React from "react";

interface InputFieldProps {
  label: string;
  name: string;
  id?: string;
  value: string | number | undefined;
  type?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  invalid?: boolean;
  placeholder: string;
  inputIcon?: React.ReactNode;
  max?: number | string;
  min?: number;
  maxLength?: number;
  required?: boolean;
  showZero?: boolean; // New prop to control whether to show 0 or hide it
  disabled?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  id,
  value,
  type = "text",
  onChange,
  onBlur = () => {},
  invalid = false,
  placeholder,
  inputIcon,
  max,
  min,
  maxLength,
  required = false,
  showZero = false, // Default to false for new entries
  disabled = false,
}) => {
  // Handle number inputs: convert 0 to empty string only if showZero is false
  const displayValue =
    type === "number" && value === 0 && !showZero ? "" : value;

  return (
    <div className="relative">
      {/* Label */}
      <label
        htmlFor={id}
        className="absolute -top-2 left-2 inline-block bg-white dark:bg-gray-900 px-1 
                 text-xs font-medium text-gray-900 dark:text-gray-200 transition-colors"
      >
        {label}
        {required && <span className="text-red-600">*</span>}
      </label>

      {/* Left icon */}
      {inputIcon && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 dark:text-gray-500">
          {inputIcon}
        </div>
      )}

      {/* Input */}
      <input
        type={type}
        name={name}
        id={id}
        value={displayValue}
        max={max}
        {...(type === "number" && {
          min: min !== undefined ? min : undefined,
          max: max !== undefined ? max : undefined,
          maxLength: maxLength !== undefined ? maxLength : undefined,
        })}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        className={`block w-full rounded-md border-0 py-2 pl-10 pr-10
                  text-gray-900 dark:text-gray-100
                  bg-white dark:bg-gray-800
                  ring-1 ring-inset ring-gray-300 dark:ring-gray-700
                  placeholder:text-gray-400 dark:placeholder:text-gray-500
                  focus:outline-none focus:ring-2 focus:ring-inset transition duration-300 ease-in-out
                  ${
                    invalid
                      ? "focus:ring-red-600"
                      : "focus:ring-green-600 dark:focus:ring-green-500"
                  }
                  sm:text-sm sm:leading-6`}
        disabled={disabled}
      />

      {/* Right icon (error state) */}
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        {invalid && (
          <ExclamationCircleIcon
            className="h-5 w-5 text-red-500"
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
};

export default InputField;
